<template>
  <div
    ref="rootRef"
    class="relative mx-auto flex w-full max-w-4xl flex-col"
    :class="[
      keyboardOpen
        ? 'min-h-0 overflow-hidden'
        : 'min-h-[calc(100dvh-64px)] md:min-h-[calc(100dvh-80px)]',
    ]"
    :style="rootStyle"
  >
    <div class="relative flex min-h-0 flex-1 flex-col">
      <div
        class="flex min-h-0 flex-1 flex-col will-change-transform"
        :class="{ 'transition-transform duration-200 ease-out': !isDragging }"
        :style="dragStyle"
      >
        <StepTransition :direction="animationDirection">
          <SingleQuestion
            @update:feedback="
              (feedback) =>
                updateFeedback({
                  ...feedback,
                  questionId: currentQuestion.id,
                  questionGroupId: currentQuestionGroup.id,
                })
            "
            :key="currentQuestion.id"
            :auto-focus-enabled="true"
            :feedback="currentFeedback"
            :hide-keyboard-shortcuts="!hasKeyboard"
            :question="currentQuestion"
            :question-group-id="currentQuestionGroup.id"
            :feedback-type="currentQuestionGroup.feedbackType"
            :scale="
              currentQuestionGroup.feedbackType != FeedbackType.Textual
                ? currentQuestionGroup.scale
                : undefined
            "
            :labels="
              currentQuestionGroup.feedbackType != FeedbackType.Textual
                ? [...currentQuestionGroup.labels]
                : []
            "
          />
        </StepTransition>
      </div>
    </div>
    <div
      v-if="hasKeyboard && !isTextualQuestion && currentQuestionGroup.scale"
      class="text-body-medium text-outline my-4 hidden flex-row items-center justify-center md:flex"
    >
      <BaseIcon size="20" class="mr-1">keyboard</BaseIcon> Tasten
      <span
        class="border-outline-variant bg-surface-container-lowest rounded-extra-small text-outline/90 text-label-small mx-1 flex h-4.75 min-w-4.5 items-center justify-center border border-b-2 px-0.5 text-center select-none"
        >1 </span
      >-
      <span
        class="border-outline-variant bg-surface-container-lowest rounded-extra-small text-outline/90 text-label-small mx-1 flex h-4.75 min-w-4.5 items-center justify-center border border-b-2 px-0.5 text-center select-none"
        >{{ currentQuestionGroup.scale.items.length }}
      </span>
      zum Antworten,
      <span
        class="border-outline-variant bg-surface-container-lowest rounded-extra-small text-outline/90 text-label-small mx-1 flex h-4.75 min-w-4.5 items-center justify-center border border-b-2 px-0.5 text-center select-none"
        >ESC
      </span>
      zum Zurücksetzen,
      <span
        class="border-outline-variant bg-surface-container-lowest rounded-extra-small text-outline/90 text-label-small ml-1 flex h-4.75 min-w-4.5 items-center justify-center border border-b-2 px-0.5 text-center select-none"
        >←
      </span>
      <span
        class="border-outline-variant bg-surface-container-lowest rounded-extra-small text-outline/90 text-label-small mx-1 flex h-4.75 min-w-4.5 items-center justify-center border border-b-2 px-0.5 text-center select-none"
        >→
      </span>
      zum Navigieren
    </div>
    <StepNavigationBar
      :items="navigationItems"
      :current-id="currentNavigationId"
      :back-disabled="!hasPreviousQuestion"
      @back="showPreviousQuestion()"
      @next="showNextQuestion()"
      @select="selectNavigationItem"
    />
  </div>
</template>
<script setup lang="ts">
  import { computed, nextTick, onMounted, ref, useTemplateRef, watch, type Ref } from 'vue'
  import { useMediaQuery, useSwipe } from '@vueuse/core'
  import { getScrollParent } from '@/core/utils/get-scroll-parent'
  import StepTransition from '@/core/components/step-flow/StepTransition.vue'
  import StepNavigationBar from '@/core/components/step-flow/StepNavigationBar.vue'
  import { FeedbackType } from '@/core/models/feedback-type'
  import SingleQuestion from '../question/SingleQuestion.vue'
  import type { QuestionGroup } from '../../models/question-group'
  import { ScaleType } from '@/core/models/scale'
  import { useGlobalKeyListener } from '@/core/composables/useGlobalKeyListener'
  import { giveFeedbackKey } from '../../composables/useGiveStructuredFeedback'
  import { injectStrict } from '../../../../core/utils/inject-strict'
  import { useVisualViewport } from '@/core/composables/use-visual-viewport'
  import { useBreakpoint } from '@/core/composables/use-break-points'
  import BaseIcon from '@/core/components/base/icon/BaseIcon.vue'

  interface Props {
    questionGroups: ReadonlyArray<QuestionGroup>
  }
  const props = defineProps<Props>()

  const rootRef = useTemplateRef<HTMLElement>('rootRef')

  // Phones and tablets report a coarse pointer and no hover. Laptops, desktops,
  // and keyboard-only devices do not.
  const hasKeyboard = useMediaQuery('(hover: hover) and (pointer: fine), (pointer: none)')

  const { onFeedbackUpdated, updateFeedback, feedbacks, state, onNavigateToQuestionById } =
    injectStrict(giveFeedbackKey)

  const scrollToTop = () => {
    if (!rootRef.value) return
    getScrollParent(rootRef.value)?.scrollTo({ top: 0 })
  }

  onFeedbackUpdated(({ feedback, question, questionGroup }) => {
    if (
      feedback.value != undefined &&
      (question.scaleItemDescriptions == undefined || question.scaleItemDescriptions.length == 0) &&
      (!question.subQuestions || question.subQuestions.length === 0) &&
      questionGroup.scale?.type !== ScaleType.Slider &&
      questionGroup.feedbackType != FeedbackType.TextualAndScaleBased
    ) {
      setTimeout(() => {
        showNextQuestion()
      }, 50)
    }
  })

  onNavigateToQuestionById(({ question }) => {
    showQuestionById(question.questionGroupId, question.questionId)
  })

  const currentQuestionGroupIndex = ref(0)
  const currentQuestionIndex = ref(0)
  const animationDirection: Ref<'next' | 'previous'> = ref('next')

  const currentQuestionGroup = computed(() => props.questionGroups[currentQuestionGroupIndex.value])
  const currentQuestion = computed(
    () => currentQuestionGroup.value.questions[currentQuestionIndex.value],
  )

  const navigationItemId = (questionGroupId: string, questionId: string) =>
    `${questionGroupId}:${questionId}`

  const currentNavigationId = computed(() =>
    navigationItemId(currentQuestionGroup.value.id, currentQuestion.value.id),
  )

  const isTextualQuestion = computed(
    () => currentQuestionGroup.value.feedbackType === FeedbackType.Textual,
  )
  const visualViewport = useVisualViewport(isTextualQuestion)
  const { breakpoints } = useBreakpoint()
  const headerHeight = computed(() => (breakpoints.greaterOrEqual('md').value ? 80 : 64))
  const keyboardOpen = computed(() => isTextualQuestion.value && visualViewport.keyboardOffset > 50)
  const rootStyle = computed(() => {
    if (!keyboardOpen.value) return undefined
    // Fit the question view into the visible area above the keyboard.
    const available = Math.max(0, visualViewport.height - headerHeight.value)
    return {
      height: `${available}px`,
      minHeight: `${available}px`,
      maxHeight: `${available}px`,
    }
  })

  watch(keyboardOpen, async (open) => {
    if (!open || !rootRef.value) return
    await nextTick()
    // Undo the browser's focus-scroll that leaves a gap under the editor.
    getScrollParent(rootRef.value)?.scrollTo({ top: 0 })
    window.scrollTo({ top: 0 })
  })

  const hasPreviousQuestion = computed(
    () => currentQuestionGroupIndex.value > 0 || currentQuestionIndex.value > 0,
  )

  const hasNextQuestion = computed(
    () =>
      currentQuestionIndex.value < currentQuestionGroup.value.questions.length - 1 ||
      currentQuestionGroupIndex.value < props.questionGroups.length - 1,
  )

  const showNextQuestion = () => {
    if (hasNextQuestion.value) {
      animationDirection.value = 'next'
      if (currentQuestionGroup.value.questions.length > currentQuestionIndex.value + 1) {
        currentQuestionIndex.value = currentQuestionIndex.value + 1
      } else if (props.questionGroups.length > currentQuestionGroupIndex.value + 1) {
        currentQuestionGroupIndex.value = currentQuestionGroupIndex.value + 1
        currentQuestionIndex.value = 0
      }

      nextTick(scrollToTop)
      return
    }

    state.value = 'finished'
    nextTick(scrollToTop)
  }

  const showPreviousQuestion = () => {
    animationDirection.value = 'previous'
    if (currentQuestionIndex.value > 0) {
      currentQuestionIndex.value = currentQuestionIndex.value - 1
    } else if (currentQuestionGroupIndex.value > 0) {
      currentQuestionGroupIndex.value = currentQuestionGroupIndex.value - 1
      currentQuestionIndex.value = currentQuestionGroup.value.questions.length - 1
    }
  }

  const isMobile = breakpoints.smaller('md')
  const ignoreSwipe = ref(false)
  const dragX = ref(0)
  const isDragging = ref(false)

  const isInteractiveSwipeTarget = (target: EventTarget | null) =>
    target instanceof Element &&
    Boolean(
      target.closest(
        'input, textarea, [contenteditable="true"], [role="slider"], input[type="range"]',
      ),
    )

  const resetDrag = (instant = false) => {
    if (instant) {
      isDragging.value = true
      dragX.value = 0
      nextTick(() => {
        isDragging.value = false
      })
      return
    }
    isDragging.value = false
    dragX.value = 0
  }

  const dragStyle = computed(() =>
    dragX.value === 0 ? undefined : { transform: `translateX(${dragX.value}px)` },
  )

  const { lengthX, lengthY } = useSwipe(rootRef, {
    threshold: 50,
    onSwipeStart(e) {
      ignoreSwipe.value =
        !isMobile.value || keyboardOpen.value || isInteractiveSwipeTarget(e.target)
    },
    onSwipe() {
      if (ignoreSwipe.value) return
      if (Math.abs(lengthX.value) <= Math.abs(lengthY.value)) {
        dragX.value = 0
        isDragging.value = false
        return
      }
      isDragging.value = true
      let next = -lengthX.value
      if (!hasPreviousQuestion.value && next > 0) next *= 0.2
      dragX.value = next
    },
    onSwipeEnd(_e, direction) {
      if (ignoreSwipe.value) {
        resetDrag(true)
        return
      }
      const isHorizontal = Math.abs(lengthX.value) > Math.abs(lengthY.value)
      const commitLeft = isHorizontal && direction === 'left'
      const commitRight = isHorizontal && direction === 'right' && hasPreviousQuestion.value

      if (commitLeft) {
        resetDrag(true)
        showNextQuestion()
        return
      }
      if (commitRight) {
        resetDrag(true)
        showPreviousQuestion()
        return
      }
      resetDrag()
    },
  })

  const selectNavigationItem = (id: string) => {
    for (const questionGroup of props.questionGroups) {
      for (const question of questionGroup.questions) {
        if (navigationItemId(questionGroup.id, question.id) === id) {
          showQuestionById(questionGroup.id, question.id)
          return
        }
      }
    }
  }

  const showQuestionById = (questionGroupId: string, questionId: string) => {
    const questionGroupIndex = props.questionGroups.findIndex(
      (group) => group.id == questionGroupId,
    )

    if (questionGroupIndex != -1) {
      const questionIndex = props.questionGroups[questionGroupIndex].questions.findIndex(
        (q) => q.id == questionId,
      )
      if (questionIndex != -1) {
        if (
          (questionGroupIndex == currentQuestionGroupIndex.value &&
            questionIndex > currentQuestionIndex.value) ||
          questionGroupIndex > currentQuestionGroupIndex.value
        ) {
          animationDirection.value = 'next'
        } else {
          animationDirection.value = 'previous'
        }
        currentQuestionGroupIndex.value = questionGroupIndex
        currentQuestionIndex.value = questionIndex
      }
    }
  }

  const currentFeedback = computed(() => {
    return feedbacks.value.find(
      (feedback) =>
        feedback.questionGroupId == currentQuestionGroup.value.id &&
        feedback.questionId == currentQuestion.value.id,
    )
  })

  const hasFeedback = (questionId: string, questionGroup: QuestionGroup) => {
    const feedback = feedbacks.value.find(
      (feedback) =>
        feedback.questionId === questionId && feedback.questionGroupId == questionGroup.id,
    )

    if (!feedback) {
      return false
    }

    if (questionGroup.feedbackType === FeedbackType.Textual) {
      return feedback.text != undefined
    }

    if (questionGroup.feedbackType === FeedbackType.ScaleBased) {
      return feedback.value != undefined
    }

    return false
  }

  const navigationItems = computed(() =>
    props.questionGroups.flatMap((questionGroup) =>
      questionGroup.questions.map((question) => ({
        id: navigationItemId(questionGroup.id, question.id),
        finished: hasFeedback(question.id, questionGroup),
      })),
    ),
  )

  const { onKey, removeKey } = useGlobalKeyListener()

  onKey('ArrowLeft', showPreviousQuestion)
  onKey('ArrowRight', showNextQuestion)

  watch(currentQuestionGroup, async (newQuestionGroup, oldQuestionGroup) => {
    removeKeyListeners(oldQuestionGroup)
    addKeyListeners(newQuestionGroup)
  })

  const removeKeyListeners = (questionGroup: QuestionGroup) => {
    if (
      questionGroup.feedbackType != FeedbackType.Textual &&
      questionGroup.scale.type != ScaleType.Bipolar
    ) {
      for (const [index] of questionGroup.scale.items.entries()) {
        removeKey((index + 1).toString())
      }
    }
  }

  const addKeyListeners = (questionGroup: QuestionGroup) => {
    if (
      questionGroup.feedbackType != FeedbackType.Textual &&
      questionGroup.scale.type != ScaleType.Bipolar
    ) {
      for (const [index, scaleItem] of questionGroup.scale.items.entries()) {
        onKey((index + 1).toString(), () => {
          updateFeedback({
            value: scaleItem.value,
            questionId: currentQuestion.value.id,
            questionGroupId: currentQuestionGroup.value.id,
          })
        })
      }

      onKey('Escape', () => {
        updateFeedback({
          value: undefined,
          questionId: currentQuestion.value.id,
          questionGroupId: currentQuestionGroup.value.id,
        })
      })
    }
  }

  onMounted(() => {
    addKeyListeners(currentQuestionGroup.value)
  })
</script>
